import { forwardRef, useEffect, useRef, useMemo, type CSSProperties } from "react";
import { Sidebar } from "../Sidebar/Sidebar";
import { Footer } from "../Footer/Footer";
import { Outlet } from "react-router-dom";
import Header from "@components/Header/Header";
import { appPageShellClassName } from "@config/theme/layout";
import { useSelector } from "react-redux";
import type { RootState } from "@store/store";
import useNotifications from "@features/Notification/useNotifications";
import { useSessionsMenuBootstrap } from "@features/Courses/hooks/useSessionsMenuBootstrap";

export const MainLayout = forwardRef<HTMLElement>(() => {
	const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
	const isExpanded = useSelector((state: RootState) => state.sidebar.isExpanded);
	const expandedWidth = useSelector((state: RootState) => state.sidebar.config.expandedWidth);
	const collapsedWidth = useSelector((state: RootState) => state.sidebar.config.collapsedWidth);
	const { fetchInitial } = useNotifications();
	const fetchedRef = useRef(false);

	useSessionsMenuBootstrap(isAuthenticated);

	const sidebarWidth = useMemo(
		() => (isExpanded ? expandedWidth : collapsedWidth),
		[isExpanded, expandedWidth, collapsedWidth]
	);

	useEffect(() => {
		if (isAuthenticated && !fetchedRef.current) {
			fetchInitial();
			fetchedRef.current = true;
		}
	}, [isAuthenticated, fetchInitial]);

	const layoutStyle = useMemo(
		() => ({ "--sidebar-offset": `${sidebarWidth}px` }) as CSSProperties,
		[sidebarWidth],
	);

	return (
		<div className="app-layout min-h-screen flex flex-col bg-[#F7FAFD]" style={layoutStyle}>
			<Header />
			<Sidebar />
			{/* z-[15]: above mobile sidebar backdrop (z-10) so main stays clickable; below sidebar (z-20) and header (z-40) */}
			<div className="relative z-[15] min-w-0 flex-1 overflow-x-hidden transition-all duration-300 ease-in-out">
				<main className={`main-content-wrapper shell-offset ${appPageShellClassName}`}>
					<Outlet />
				</main>
			</div>
			<Footer />
		</div>
	);
});
